import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
// actions
import appAction from '../../../redux/actions/app';
import strings from '../../../localization';
// types
import { stateType } from '../../../types/types';
// style
import './index.scss';

interface HeaderMenuProps {
  isIntegration: boolean;
}

// Builds two-letter initials from the user's first and last name.
function getInitials(firstName: string, lastName: string): string {
  const first = (firstName || '').trim()[0] || '';
  const last = (lastName || '').trim()[0] || '';

  if (first || last) {
    return `${first}${last}`.toUpperCase();
  }

  return '?';
}

// Returns a display name or a placeholder when names are missing.
function getDisplayName(firstName: string, lastName: string): string {
  const name = `${(firstName || '').trim()} ${(lastName || '').trim()}`.trim();
  return name || '—';
}

// Maps account type to a localized role label for the header.
function getRoleLabel(userType: string): string {
  const type = (userType || '').toLowerCase();

  if (type === 'patient') {
    return strings.header.headerMenu.rolePatient;
  }

  if (type === 'doctor') {
    return strings.header.headerMenu.roleDoctor;
  }

  return '';
}

// Renders the user profile block with a dropdown menu (logout).
const HeaderMenu: FC<HeaderMenuProps> = ({ isIntegration }: HeaderMenuProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const { firstName, lastName, userType } = useSelector((state: stateType) => state.user.user);

  const displayName = getDisplayName(firstName, lastName);
  const roleLabel = getRoleLabel(userType);
  const initials = getInitials(firstName, lastName);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    // Delay adding listener so the current tap that opened the menu doesn't immediately close it.
    const timerId = setTimeout(() => {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('keydown', handleKeyDown);
    }, 300);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Clears session and navigates to login after successful logout.
  const handleLogout = () => {
    setIsOpen(false);

    const callback = () => {
      sessionStorage.setItem('clearLoginForm', '1');
      history.push('/login');
    };

    dispatch(appAction.logout(callback));
  };

  // Toggles the profile dropdown when the avatar is clicked.
  const handleAvatarClick = () => {
    setIsOpen((open) => !open);
  };

  if (isIntegration) {
    return null;
  }

  return (
    <div className="header-menu" ref={menuRef}>
      <div className="header-menu__profile">
        <div className="header-menu__identity">
          <span className="header-menu__name" title={displayName}>
            {displayName}
          </span>
          {roleLabel && <span className="header-menu__role">{roleLabel}</span>}
        </div>
        <button
          type="button"
          className="header-menu__avatar"
          onClick={handleAvatarClick}
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label={displayName}
          title={displayName}
        >
          <span aria-hidden="true">{initials}</span>
        </button>
      </div>
      <div className={`header-menu__dropdown${isOpen ? ' header-menu__dropdown--open' : ''}`} role="menu">
        <div className="header-menu__dropdown-item" onClick={handleLogout} role="menuitem">
          {strings.header.headerMenu.logout}
        </div>
      </div>
    </div>
  );
};

export default HeaderMenu;
